import React, { useCallback, useMemo, useState } from 'react'
import { Form } from 'react-bootstrap'
import { firstApiErrorMessage } from '../../../models/ApiErrors'
import { Modal } from '../../../components/Modal/Modal'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import { addToast } from '../../../store/toast/toastSlice'
import { useUpdateDataSourceMutation } from '../../../store/integrations/externalDataSources'
import { type ExternalDataSource } from '../../../models/ExternalDataSource'
import { DataSourceDocsLink } from './DataSourceDocsLink'
import { DataSourceFormFields } from './DataSourceFormFields'
import { buildUpdateBody, type DataSourceFormValues, isDataSourceFormValid } from './dataSourceForm'

interface EditDataSourceModalProps {
  show: boolean
  clientId: string
  dataSource: ExternalDataSource
  onHide: () => void
  onExited?: () => void
}

const toFormValues = (dataSource: ExternalDataSource): DataSourceFormValues => ({
  name: dataSource.name,
  description: dataSource.description ?? '',
  callbackUrl: dataSource.callbackUrl,
  dataTypes: [...dataSource.dataTypes],
  contractVersion: dataSource.contractVersion,
  timeoutMs: String(dataSource.fetchPolicy.timeoutMs),
  useApiKey: dataSource.auth !== null,
  headerName: dataSource.auth?.headerName ?? 'X-Api-Key',
  // Never returned by a read, so the field starts blank and blank means "keep it".
  apiKey: '',
})

export const EditDataSourceModal: React.FC<EditDataSourceModalProps> = ({ show, clientId, dataSource, onHide, onExited }) => {
  const dispatch = useAppDispatch()
  const accountId = useAppSelector(selectAccountId)
  const [updateDataSource, { isLoading }] = useUpdateDataSourceMutation()

  const [values, setValues] = useState<DataSourceFormValues>(() => toFormValues(dataSource))
  const [validated, setValidated] = useState<boolean>(false)

  const storedApiKey = useMemo(() => (dataSource.auth === null ? null : { headerName: dataSource.auth.headerName }), [dataSource.auth])
  const body = useMemo(() => buildUpdateBody(dataSource, values), [dataSource, values])
  const hasChanges = Object.keys(body).length > 0
  const isValid = isDataSourceFormValid(values, storedApiKey)

  const handleSave = useCallback(async () => {
    setValidated(true)
    if (!isValid || !hasChanges) {
      return
    }
    try {
      await updateDataSource({ accountId, clientId, dataSourceId: dataSource.id, payload: body }).unwrap()
      dispatch(
        addToast({
          variant: 'success',
          title: 'Data source updated',
          messages: ['It is back to pending — verify and test it again before Zocks will pull from it.'],
        }),
      )
      onHide()
    } catch (error) {
      dispatch(addToast({ variant: 'error', title: 'Could not update the data source', messages: [firstApiErrorMessage(error) ?? 'Please try again.'] }))
    }
  }, [isValid, hasChanges, updateDataSource, accountId, clientId, dataSource.id, body, dispatch, onHide])

  return (
    <Modal
      show={show}
      onHide={onHide}
      onExited={onExited}
      header={
        <span className='d-flex align-items-center justify-content-between flex-grow-1 zocks-gap-1 me-2'>
          {`Edit ‘${dataSource.name}’`}
          <DataSourceDocsLink />
        </span>
      }
      body={
        <Form>
          {dataSource.status === 'active' ? (
            <div className='d-flex align-items-start zocks-gap-0.5 p-3 mb-3 rounded-2 zocks-bg-color-warning'>
              <i className='bi bi-exclamation-triangle-fill zocks-text-warning-text flex-shrink-0' />
              <p className='paragraph-small mb-0'>
                This data source is live. Saving any change returns it to <strong>pending</strong> and clears its verification, so Zocks stops pulling from it
                until you verify and test it again — for every account that enabled this app.
              </p>
            </div>
          ) : null}

          <DataSourceFormFields values={values} onChange={setValues} validated={validated} storedApiKey={storedApiKey} />

          <p className='paragraph-small zocks-text-gray-600 mb-0'>The signing secret does not change. Editing never re-issues it.</p>
        </Form>
      }
      showFooterActions
      footerPrimaryActionTitle='Save changes'
      footerSecondaryActionTitle='Cancel'
      onFooterPrimaryAction={handleSave}
      onFooterSecondaryAction={onHide}
      footerPrimaryActionLoading={isLoading}
      footerPrimaryActionDisabled={isLoading || !hasChanges}
      footerPrimaryActionDescription={hasChanges ? '' : 'Nothing has changed yet.'}
    />
  )
}
